import type { CreditTaxLine } from '../api/billing';

// ════════════════════════════════════════════════════════════════════════════
// taxLine — the PURE phrasing of the portal's Stripe-computed tax line (Stripe Tax adoption Phase 3,
// server-config/plans/stripe-tax-adoption.md § Phase 3: "purchase history rows show tax amount + buyer
// country"; voice-assistant issue #2176). Pinned by frontend/src/shared-ui/taxLine.spec.ts and
// setpoint-evals/stripe-tax-p3/SE-02.
//
// It only PHRASES what the backend read off `stripe_tax_record` (Stripe's own computation): it never derives
// a rate, never turns an absent fact into a zero, and prints nothing for a fact that is missing. There is no
// rate constant in this file — a percentage appears ONLY when the payload states one (SE-02 scans this file
// and the components for the forms a hand-typed rate takes).
// ════════════════════════════════════════════════════════════════════════════

/** The phrased parts of one purchase's tax line; either part may be null (never both — that is `null` overall). */
export interface TaxLineText {
  /** "incl. VAT EUR 6.39 (19%)" · "No VAT charged" · "No VAT charged (reverse charge)" — null when Stripe's amount is unknown. */
  vat: string | null;
  /** "Billing country: DE" — null when the country is absent or malformed. */
  country: string | null;
}

/** Minor units → "EUR 2.50". An unknown currency prints the bare number — never a guessed one. */
export function formatMoney(minor: number, currency: string | null | undefined): string {
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(minor));
  const num = `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  const cur = typeof currency === 'string' && currency.length > 0 ? currency.toUpperCase() : null;
  return cur ? `${cur} ${num}` : num;
}

/** A rate is shown only when Stripe stated one and it is a positive figure (a stated 0 next to a positive amount is inconsistent). */
function ratePart(ratePercent: number | null | undefined): string {
  return typeof ratePercent === 'number' && Number.isFinite(ratePercent) && ratePercent > 0 ? ` (${ratePercent}%)` : '';
}

/**
 * Phrase one purchase's tax. `null` when there is nothing to say — no tax view, or one that states neither a usable
 * amount nor a valid country (a legacy purchase renders NO tax block, never a "0").
 */
export function describeTax(tax: CreditTaxLine | null | undefined): TaxLineText | null {
  if (!tax) return null;
  const amount = typeof tax.amountTaxMinor === 'number' && Number.isFinite(tax.amountTaxMinor) && tax.amountTaxMinor >= 0 ? tax.amountTaxMinor : null;

  let vat: string | null = null;
  if (amount !== null && amount > 0) vat = `incl. VAT ${formatMoney(amount, tax.currency)}${ratePart(tax.ratePercent)}`;
  else if (amount === 0) vat = tax.taxabilityReason === 'reverse_charge' ? 'No VAT charged (reverse charge)' : 'No VAT charged';

  const cc = typeof tax.country === 'string' && /^[A-Za-z]{2}$/.test(tax.country) ? tax.country.toUpperCase() : null;
  const country = cc ? `Billing country: ${cc}` : null;

  return vat === null && country === null ? null : { vat, country };
}
